import { TouchEvent, useEffect, useRef } from 'react';

type TouchControlType = 'dpad' | 'updown' | 'tap';
type Direction = 'up' | 'down' | 'left' | 'right';

interface TouchControlsProps {
  type: TouchControlType;
  onDirection: (dir: Direction) => void;
  onAction: () => void;
  onQuit: () => void;
  actionLabel?: string;
}

export function TouchControls({ type, onDirection, onAction, onQuit, actionLabel }: TouchControlsProps) {
  const holdIntervalRef = useRef<number | null>(null);

  const preventTouchDefault = (event: TouchEvent) => {
    event.preventDefault();
  };

  const fireDirection = (event: TouchEvent, direction: Direction) => {
    event.preventDefault();
    onDirection(direction);
  };

  const startHoldDirection = (event: TouchEvent, direction: 'up' | 'down') => {
    event.preventDefault();
    onDirection(direction);

    if (holdIntervalRef.current) {
      window.clearInterval(holdIntervalRef.current);
    }

    holdIntervalRef.current = window.setInterval(() => {
      onDirection(direction);
    }, 70);
  };

  const stopHoldDirection = (event: TouchEvent) => {
    event.preventDefault();
    if (holdIntervalRef.current) {
      window.clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }
  };

  const handleQuit = (event: TouchEvent) => {
    event.preventDefault();
    onQuit();
  };

  const handleAction = (event: TouchEvent) => {
    event.preventDefault();
    onAction();
  };

  useEffect(() => {
    return () => {
      if (holdIntervalRef.current) {
        window.clearInterval(holdIntervalRef.current);
      }
    };
  }, []);

  return (
    <div className="touch-controls-overlay" onTouchMove={preventTouchDefault}>
      <button className="touch-btn touch-quit-btn" type="button" onTouchStart={handleQuit} aria-label="Quit game">
        ✕ QUIT
      </button>

      {type === 'dpad' ? (
        <div className="touch-controls touch-controls-dpad">
          <div className="touch-dpad-top">
            <button className="touch-btn touch-btn-arrow" type="button" onTouchStart={(event) => fireDirection(event, 'up')}>
              ▲
            </button>
          </div>
          <div className="touch-dpad-middle">
            <button
              className="touch-btn touch-btn-arrow"
              type="button"
              onTouchStart={(event) => fireDirection(event, 'left')}
            >
              ◄
            </button>
            <button
              className="touch-btn touch-btn-arrow"
              type="button"
              onTouchStart={(event) => fireDirection(event, 'down')}
            >
              ▼
            </button>
            <button
              className="touch-btn touch-btn-arrow"
              type="button"
              onTouchStart={(event) => fireDirection(event, 'right')}
            >
              ►
            </button>
          </div>
          {actionLabel ? (
            <div className="touch-dpad-action">
              <button className="touch-btn touch-btn-action" type="button" onTouchStart={handleAction}>
                {actionLabel}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {type === 'updown' ? (
        <div className="touch-controls touch-controls-updown">
          <button
            className="touch-btn touch-btn-tall"
            type="button"
            onTouchStart={(event) => startHoldDirection(event, 'up')}
            onTouchEnd={stopHoldDirection}
            onTouchCancel={stopHoldDirection}
          >
            ▲ UP
          </button>
          <button
            className="touch-btn touch-btn-tall"
            type="button"
            onTouchStart={(event) => startHoldDirection(event, 'down')}
            onTouchEnd={stopHoldDirection}
            onTouchCancel={stopHoldDirection}
          >
            ▼ DOWN
          </button>
        </div>
      ) : null}

      {type === 'tap' ? (
        <div className="touch-controls touch-controls-tap">
          <button className="touch-btn touch-btn-wide" type="button" onTouchStart={handleAction}>
            TAP TO JUMP
          </button>
        </div>
      ) : null}
    </div>
  );
}
